import { PlusIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { merge, setWith } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import NoItemsRow from "../../common/NoItemsRow";
import { validateFloat } from "../../common/validation/Validators";
import NICtoDateRow from "./NICtoDateRow";

const TotalsToDate = ({
	totalsToDate,
	confirmModal,
	disabled,
	forNewEmployee,
	updateTotalsToDate,
	nextPage,
	prevPage,
	NIlettersAndValues,
	currency,
	forReviewAgreement,
	forReview,
	nicLetters,
	confirmChange,
	notification,
	getTotalsToDate,
	jurisdiction,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};
	const updateArrayData = ({ array, index, newItem }) => {
		const currentValue = data[array][index] || {};
		Object.entries({ ...newItem }).map((item) => {
			const [key, value] = item;
			currentValue[key] = value ?? currentValue[key];
		});

		data[array].splice(index, 1, currentValue);
		setData(cleanDeep({ ...data }));
	};

	useEffect(() => {
		if (jurisdiction != "UK") return
		getTotalsToDate();
	}, [!!totalsToDate]);

	const totalsToDateID = useMemo(() => {
		const newID = String(totalsToDate?.id);
		if (newID === "undefined") return null;
		return newID;
	}, [totalsToDate?.id]);

	useEffect(() => {
		const hasTotalsToDate = totalsToDate && Object.keys(cleanDeep(totalsToDate))?.length > 0;
		if (!hasTotalsToDate) return;
		const fields = [
			"year",
			"TaxablePay",
			"TotalTax",
			"StudentLoansTD",
			"PostgradLoansTD",
			"BenefitsTaxedViaPayrollYTD",
			"Class1ANICsYTD",
			"SMPYTD",
			"SPPYTD",
			"SAPYTD",
			"ShPPYTD",
			"SBPYTD",
			"EmpPenContribnsPaidYTD",
			"EmpeePenContribnsPaidYTD",
			"EmpeePenContribnsNotPaidYTD",
			"EmpeePenSalarySacrificeYTD",
			"NIlettersAndValues",
		];
		const newData = Object.entries(cleanDeep(totalsToDate))?.reduce(
			(prev, curr) => {
				const [field, value] = curr;
				if (fields.includes(field)) prev[field] = value || 0;
				return prev;
			},
			{ year: forReview ? totalsToDate?.year : undefined },
		);
		setData({ ...newData });
	}, [totalsToDateID]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (forNewEmployee) {
			updateTotalsToDate(cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }));
			nextPage();
			return;
		}

		confirmModal({
			input: cleanDeep(
				{ ...data, year: data.year?.substr(0, 4) },
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
			cannotReplace: !totalsToDate?.id,
			cannotCreateNew: !!totalsToDate?.id,
			status: true,
			dateType: "taxYear",
			formType: "Totals to Date",
		});

		if (!forReview) return;
		const newDate = forReview?.originalDate && data.start != forReview?.originalDate;
		confirmChange(
			newDate || !forReview.originalDate ? data.start : forReview.originalDate,
			!newDate,
			forReviewAgreement,
			notification,
		);
	};

	if (jurisdiction != "UK") return null

	return (
		<form
			onSubmit={onSubmit}
			className="pb-20 px-8"
		>
			<div className="col-xs-12 form-section">
				<div className="col-xs-3 section-header">
					<h1 className="block-title">Totals To Date</h1>
				</div>
				<div className="col-xs-3">
					{data.year ? (
						<div
							className="form-material"
							style={{ paddingLeft: 0 }}
						>
							<label htmlFor="material-text">{"Dated For"}</label>
							<input
								value={data.year ? `${data.year}/${String(Number(data.year) + 1).substring(2, 4)}` : ""}
								className="form-control"
								disabled={true}
							/>
						</div>
					) : null}
				</div>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Figures To Date</h1>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="TaxablePay"
						name="TaxablePay"
						autoComplete="off"
						value={data.TaxablePay}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="TaxablePay"
						className="pl-6"
					>
						Taxable Pay
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="TotalTax"
						name="TotalTax"
						autoComplete="off"
						value={data.TotalTax}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="TotalTax"
						className="pl-6"
					>
						Total Tax
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="BenefitsTaxedViaPayrollYTD"
						name="BenefitsTaxedViaPayrollYTD"
						autoComplete="off"
						value={data.BenefitsTaxedViaPayrollYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="BenefitsTaxedViaPayrollYTD"
						className="pl-6"
					>
						Benefits Taxed
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="Class1ANICsYTD"
						name="Class1ANICsYTD"
						autoComplete="off"
						value={data.Class1ANICsYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="Class1ANICsYTD"
						className="pl-6"
					>
						Class 1A NICs
					</label>
				</div>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Student Loans</h1>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="StudentLoansTD"
						name="StudentLoansTD"
						autoComplete="off"
						value={data.StudentLoansTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="StudentLoansTD"
						className="pl-6"
					>
						Student Loans
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="PostgradLoansTD"
						name="PostgradLoansTD"
						autoComplete="off"
						value={data.PostgradLoansTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="PostgradLoansTD"
						className="pl-6"
					>
						Postgraduate Loans
					</label>
				</div>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Statutory Payments</h1>
				</div>
				<div className="col-xs-2 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="SMPYTD"
						name="SMPYTD"
						autoComplete="off"
						value={data.SMPYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="SMPYTD"
						className="pl-6"
					>
						Maternity Pay
					</label>
				</div>
				<div className="col-xs-2 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="SPPYTD"
						name="SPPYTD"
						autoComplete="off"
						value={data.SPPYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="SPPYTD"
						className="pl-6"
					>
						Paternity Pay
					</label>
				</div>
				<div className="col-xs-2 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="SAPYTD"
						name="SAPYTD"
						autoComplete="off"
						value={data.SAPYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="SAPYTD"
						className="pl-6"
					>
						Adoption Pay
					</label>
				</div>
				<div className="col-xs-2 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="ShPPYTD"
						name="ShPPYTD"
						autoComplete="off"
						value={data.ShPPYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="ShPPYTD"
						className="pl-6"
					>
						Shared Parental Pay
					</label>
				</div>
				<div className="col-xs-2 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="SBPYTD"
						name="SBPYTD"
						autoComplete="off"
						value={data.SBPYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="SBPYTD"
						className="pl-6"
					>
						Bereavement Pay
					</label>
				</div>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Pension Contributions</h1>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="EmpPenContribnsPaidYTD"
						name="EmpPenContribnsPaidYTD"
						autoComplete="off"
						value={data.EmpPenContribnsPaidYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="EmpPenContribnsPaidYTD"
						className="pl-6"
					>
						Employer Pension
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="EmpeePenContribnsPaidYTD"
						name="EmpeePenContribnsPaidYTD"
						autoComplete="off"
						value={data.EmpeePenContribnsPaidYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="EmpeePenContribnsPaidYTD"
						className="pl-6"
					>
						Employee Pension (Net Pay Arrangement)
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="EmpeePenContribnsNotPaidYTD"
						name="EmpeePenContribnsNotPaidYTD"
						autoComplete="off"
						value={data.EmpeePenContribnsNotPaidYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="EmpeePenContribnsNotPaidYTD"
						className="pl-6"
					>
						Employee Pension (Relief At Source)
					</label>
				</div>
				<div className="col-xs-3 form-material">
					<div style={{ position: "absolute", top: 5 }}>{currency}</div>
					<input
						style={{ paddingLeft: "1rem" }}
						className="form-control"
						disabled={disabled}
						id="EmpeePenSalarySacrificeYTD"
						name="EmpeePenSalarySacrificeYTD"
						autoComplete="off"
						value={data.EmpeePenSalarySacrificeYTD}
						onChange={(e) => {
							updateField(e.target.name, validateFloat(e.target.value));
						}}
					/>
					<label
						htmlFor="EmpeePenSalarySacrificeYTD"
						className="pl-6"
					>
						Employee Salary Sacrifice
					</label>
				</div>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-6 section-header">
					<h1 className="block-title">Nicable Income</h1>
				</div>
				<div className="col-xs-6">
					<button
						className="btn btn-success pull-right"
						disabled={
							disabled ||
							(NIlettersAndValues?.length || 1) * (nicLetters?.length || 1) ==
								data.NIlettersAndValues?.length
						}
						type="button"
						onClick={() => {
							updateField("NIlettersAndValues", [
								...(Array.isArray(data.NIlettersAndValues) ? data.NIlettersAndValues : []),
								{},
							]);
						}}
					>
						<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Add NIC Band</div>
					</button>
				</div>
				<div className="clearfix" />
				{data.NIlettersAndValues?.length > 0 ? (
					<>
						{data.NIlettersAndValues?.map((band, index) => {
							return (
								<NICtoDateRow
									index={index}
									NIlettersAndValues={data.NIlettersAndValues}
									nicLettersList={nicLetters}
									disabled={disabled}
									updateNicBand={({
										NIletter,
										GrossEarningsForNICsYTD,
										AtLELYTD,
										LELtoPTYTD,
										PTtoUELYTD,
										TotalEmpNICYTD,
										EmpeeContribnsYTD,
									}) => {
										updateArrayData({
											array: "NIlettersAndValues",
											index,
											newItem: {
												NIletter,
												GrossEarningsForNICsYTD,
												AtLELYTD,
												LELtoPTYTD,
												PTtoUELYTD,
												TotalEmpNICYTD,
												EmpeeContribnsYTD,
											},
										});
									}}
									setNicBands={(NIlettersAndValues) => setData({ ...data, NIlettersAndValues })}
								/>
							);
						})}
					</>
				) : (
					<table className="js-table-sections table table-hover">
						<NoItemsRow name="NIC Bands" />
					</table>
				)}
			</div>
			<div className="clearfix" />
			<div>
				{!disabled && (
					<button
						className="btn btn-success right"
						type="submit"
					>
						{!totalsToDateID ? `Save${forNewEmployee ? " & Continue" : ""}` : "Update Totals to Date"}
					</button>
				)}
				{forNewEmployee && (
					<>
						<button
							style={{ marginLeft: "1rem" }}
							className="btn btn-default pull-right"
							onClick={nextPage}
						>
							Skip
						</button>
						{prevPage && (
							<button
								className="btn btn-default"
								onClick={prevPage}
							>
								Back
							</button>
						)}
					</>
				)}
			</div>
		</form>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { forReview, forNewEmployee } = ownProps;

	let totalsToDate = state.employee?.totalsToDate?.[state.employee?.totalsToDate?.length - 1];
	if (forNewEmployee) totalsToDate = state.employee.totalsToDate?.[0] || {};
	if (forReview) totalsToDate = forReview;
	const { niLetters: nicLetters } = { ...state.taxOptions };

	return {
		nicLetters,
		jurisdiction: state.company?.settings?.jurisdiction,
		currency: state.company?.settings?.currency,
		totalsToDate,
		engagementDate: state.employee?.employeeDetails?.engagementDate,
		disabled: state?.permissions?.my?.permissions?.people == "Read",
	};
};

const mapDispatchToProps = (dispatch) => ({
	openPEImportModal: () => dispatch(EMPLOYEEACTIONS.togglePEImportModal(true)),
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
	getTotalsToDate: () => dispatch(EMPLOYEEACTIONS.getTotalsToDate()),
	updateTotalsToDate: (input, agreement, date) =>
		dispatch(EMPLOYEEACTIONS.updateTotalsToDate(input, agreement, date)),
	confirmChange: (date, replace, agreement, notification) =>
		dispatch(EMPLOYEEACTIONS.confirmChange({ date, replace, agreement, notification })),
});

export default connect(mapStateToProps, mapDispatchToProps)(TotalsToDate);
